<script setup>
import { computed } from 'vue'
import BcsTooltip from '../BcsTooltip.vue'

const props = defineProps({
  text: {
    type: String,
    required: true
  },
  color: {
    type: String,
    default: 'neutral',
    validator: (val) => ['success', 'warning', 'danger', 'primary', 'neutral', 'muted', 'gray'].includes(val)
  },
  tooltipText: {
    type: String,
    default: ''
  },
  contentClass: {
    type: String,
    default: ''
  },
  bold: {
    type: Boolean,
    default: true
  }
})

const hasTooltip = computed(() => !!props.tooltipText)

const pillClass = computed(() => {
  const map = {
    success: 'bcs-pill-success',
    warning: 'bcs-pill-warning',
    danger: 'bcs-pill-danger',
    primary: 'bcs-pill-primary',
    neutral: 'bcs-pill-neutral',
    muted: 'bcs-pill-muted',
    gray: 'bcs-pill-gray'
  }
  return map[props.color] || 'bcs-pill-neutral'
})
</script>

<template>
  <BcsTooltip
    v-if="hasTooltip"
    :simpleText="tooltipText"
    :show-arrow="true"
    :contentClass="'simple-text-tooltip'"
  >
    <span class="bcs-status-pill" :class="[pillClass, contentClass, { 'has-tooltip': true }]">
      <strong v-if="bold">{{ text }}</strong>
      <template v-else><span class="fw-medium">{{ text }}</span></template>
    </span>
  </BcsTooltip>
  <span v-else class="bcs-status-pill" :class="[pillClass, contentClass]">
    <strong v-if="bold">{{ text }}</strong>
    <template v-else><span class="fw-medium">{{ text }}</span></template>
  </span>
</template>

<style scoped lang="scss">
.bcs-status-pill {
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  white-space: nowrap;
  border: 1px solid;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.has-tooltip {
  cursor: default;
}

.bcs-pill-success {
  color: var(--green-500);
  border-color: var(--green-500);
  background-color: var(--green-060);
}

.bcs-pill-warning {
  color: var(--warning-500);
  border-color: var(--warning-500);
  background-color: var(--yellow-100);
}

.bcs-pill-danger {
  color: var(--error-500);
  border-color: var(--error-500);
  background-color: var(--error-050);
}

.bcs-pill-primary {
  color: var(--bs-primary);
  border-color: var(--bs-primary);
  background-color: #f7f1fd;
}

.bcs-pill-neutral {
  color: #495057;
  border-color: #495057;
  background-color: #fff;
}

.bcs-pill-muted {
  color: var(--bs-gray-600);
  border-color: var(--bs-gray-600);
  background-color: #fff;
}

/* Gray on a gray fill (Figma): unlike `muted`, which sits on white. */
.bcs-pill-gray {
  color: #6c757e;
  border-color: #6c757e;
  background-color: #edf0f2;
}
</style>
